import type { FleetLaneEngine, FleetLaneEngineRotation } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';

/**
 * The engine and models a lane's next round runs on, and where that choice came from (fleet-agent 0.19.0). The
 * models arrive resolved, so this shows what runs without knowing any adapter's defaults. The orchestrator is
 * named only when it differs — a project's `stepEngine` moves the steps alone.
 */
export function LaneEngine({ engine }: { readonly engine: FleetLaneEngine }) {
  const t = useTranslations('pages.admin.fleet.engine');

  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="text-sm text-text-tertiary">{t('title')}</span>
      <span className="min-w-0 break-words font-mono text-base-sm text-text-primary">
        {engine.engine}
        {engine.orchestrator !== engine.engine && ` (${t('orchestrator', { engine: engine.orchestrator })})`}
      </span>
      <span className="min-w-0 break-words font-mono text-sm text-text-secondary">
        {t('models', { plan: engine.planModel, implement: engine.implementModel })}
      </span>
      <span className="rounded-sm bg-neutral-100 px-1.5 py-0.5 text-xs text-text-tertiary">
        {t(`source.${engine.source}`)}
      </span>
    </div>
  );
}

/** A lane under cross-engine rotation (IMM-1452): its pin, or any engine, and the engine of the round in flight. */
export function LaneRotation({ rotation }: { readonly rotation: FleetLaneEngineRotation }) {
  const t = useTranslations('pages.admin.fleet.engine');

  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="text-sm text-text-tertiary">{t('title')}</span>
      <span className="min-w-0 break-words font-mono text-base-sm text-text-primary">
        {rotation.pin === null ? t('any') : t('pinned', { engine: rotation.pin })}
      </span>
      {rotation.running !== null && (
        <span className="min-w-0 break-words font-mono text-sm text-text-secondary">
          {t('running', { engine: rotation.running })}
        </span>
      )}
    </div>
  );
}
